import React from 'react';
import { Link } from 'react-router-dom';

const TEAM = [
  {
    initials: 'FS', name: 'Frederic Scheer, PhD', title: 'CEO & Chief Scientific Officer',
    bio: 'PhD, Université de Paris and training at MIT on AI in Healthcare. Certificates in Precision Oncology & DNA Sequencing at Harvard Medical School. NSF/NIH Principal Investigator. Founder of Alercell, Inc. and architect of the LENA platform™ and Kinetic Information Theory framework. Deep expertise in epigenetics, AI-driven diagnostics, and translational oncology.',
  },
  {
    initials: 'AS', name: 'Dr. Alexandre Scheer, MD', title: 'Chief Medical Officer',
    bio: 'Medical Doctor with clinical training in surgical oncology. Co-founder of Alercell. Brings direct patient care perspective to the design and clinical implementation of the LENA diagnostic platform, ensuring that technological innovation translates into actionable clinical decision support.',
  },
  {
    initials: 'DM', name: 'Donna Mendes, MD', title: 'Medical Advisor',
    bio: 'First African American woman to become ABS-certified in vascular surgery (1986). Professor of Surgery, Columbia University since 1990. Decorated physician and clinical leader whose expertise in surgical oncology and hospital systems informs Alercell\'s clinical translation and implementation strategy.',
  },
  {
    initials: 'GS', name: 'Prof. Gleb Sukhorukov', title: 'Scientific Advisor',
    bio: 'Professor of Biomaterials at Queen Mary University of London. International expert in nanomedicine, drug delivery, and bioencapsulation. Provides strategic scientific guidance on next-generation diagnostic and theranostic delivery mechanisms.',
  },
  {
    initials: 'ET', name: 'Dr. Elina Tsyvkin, MD', title: 'Medical Advisor',
    bio: 'MD from University of Moscow, followed by residency at Tufts University and UMass University. Currently holds a position in Hematology Malignancies/Leukemia at Memorial Sloan Kettering Cancer Center. Over 25 years of experience in hematologic malignancies. Associate Professor at Cornell University Medical School.',
  },
];

export default function About() {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Our Team & Mission</span>
          <h1 className="hero-headline" style={{ maxWidth: 780, marginBottom: 24 }}>
            Built by scientists. Validated by data.<br />Driven by a single clinical imperative.
          </h1>
          <p className="body-lg" style={{ maxWidth: 640 }}>
            Alercell, Inc. is a precision oncology company building autonomous AI diagnostics on a SaaS platform for early leukemia detection, enabled by proprietary epigenetic biomarkers. Headquartered in Bozeman, Montana. Science validated across 199,000+ de-identified patient records.
          </p>
        </div>
      </section>

      {/* Welcome Video */}
      <section style={{ background: 'var(--bg-surface)', padding: '60px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="about-split">
            <div>
              <span className="eyebrow">A Word from the Chairman</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
                Frederic Scheer, PhD — Founder & CEO
              </h2>
              <p className="body-md">
                Hear directly from Frederic Scheer on why Alercell was founded, the science behind the LENA platform, and the clinical imperative driving early leukemia detection.
              </p>
            </div>
            <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', aspectRatio: '16/9' }}>
              <iframe
                width="100%" height="100%"
                src="https://www.youtube.com/embed/Umm_r6gAxXg"
                title="A Word from the Chairman — Frederic Scheer"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ display: 'block' }}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container">
          <span className="eyebrow">The Problem We Are Solving</span>
          <h2 className="section-headline" style={{ maxWidth: 780, marginBottom: 24 }}>
            Cancer kills more than 1,600 Americans every day. Most of them did not have to die when they did.
          </h2>
          <div className="cyan-line" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60 }} className="about-split">
            <div>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Cancer is the second leading cause of death in the United States. In 2025, more than 2 million Americans will receive a cancer diagnosis, and 618,120 will die from the disease — a rate of approximately 1,694 deaths per day.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Despite a 34% decline in the overall cancer mortality rate since 1991, six of the ten most commonly diagnosed cancers are now increasing in frequency — including both forms of acute leukemia in adults. Approximately 38.9% of Americans will receive a cancer diagnosis at some point in their lifetime.
              </p>
              <p className="source-cite">Source: Siegel RL et al. Cancer Statistics, 2025. CA: A Cancer Journal for Clinicians. DOI:10.3322/caac.21871</p>
            </div>
            <div>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Blood cancers — leukemia, lymphoma, and myeloma — collectively account for 9.4% of all new cancer diagnoses and 9.1% of all cancer deaths in the United States. Unlike breast, colorectal, and cervical cancers, hematologic malignancies have no established general-population screening protocol.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                The failure to detect cancer early is not only a clinical tragedy — it is a systemic economic failure. The direct medical costs of cancer care reached $183 billion in 2015, with projections estimating $296 billion by 2030. Medicare beneficiaries diagnosed at later stages incur up to 7× higher treatment costs than those diagnosed early.
              </p>
              <p className="source-cite">Source: NCI Cancer Trends Progress Report, 2024; BMC Health Services Research, 2022</p>
            </div>
          </div>

          <div className="stat-grid">
            {[
              { value: '200,000+', label: 'Americans diagnosed with blood cancer annually' },
              { value: '56,000+', label: 'Blood cancer deaths per year in the United States' },
              { value: '0', label: 'General-population screening protocols for leukemia' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 800, color: 'var(--accent-cyan)', display: 'block', marginBottom: 8 }}>{s.value}</span>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media(max-width:768px){.about-split{grid-template-columns:1fr!important;}}`}</style>
      </section>

      {/* Company History */}
      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }} className="about-split">
            <div>
              <span className="eyebrow">Company History</span>
              <h2 className="section-headline" style={{ marginBottom: 20 }}>
                Leukemia is survivable, when caught in time. We built the platform to catch it earlier.
              </h2>
              <div className="cyan-line" />
              <p className="body-md" style={{ marginBottom: 20 }}>
                Acute myeloid leukemia carries a five-year survival rate below 30% when diagnosed at advanced stages. Yet the biological precursors of AML — epigenetic drift patterns in hematopoietic stem cells — are detectable years before any genetic mutation becomes visible.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Alercell, Inc. was established in Bozeman, Montana, at the intersection of artificial intelligence and molecular oncology. Our founding insight: DNA methylation patterns, analyzed at scale with purpose-built AI, carry a predictive signal for leukemia that conventional genetic sequencing cannot provide — because methylation changes precede mutation, not the other way around.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Bozeman is not an accident. Montana State University provides a world-class computational biology and bioengineering talent ecosystem within walking distance of our headquarters — enabling exceptional scientific staff outside the cost structure of coastal biotech hubs.
              </p>
              <p className="body-md" style={{ fontStyle: 'italic', color: 'var(--text-primary)', borderLeft: '3px solid var(--accent-cyan)', paddingLeft: 20 }}>
                Our mission is not to treat cancer better. It is to detect it early enough that aggressive treatment becomes, in most cases, unnecessary.
              </p>
            </div>

            {/* Timeline */}
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 32 }}>Milestones</h3>
              <div className="timeline">
                {[
                  { year: '2020', title: 'Alercell, Inc. Founded', desc: 'Incorporated in Bozeman, Montana. Core epigenetic hypothesis formulated. Initial dataset assembly begins.' },
                  { year: '2022', title: 'LENA Q51® Launched', desc: 'First commercially available diagnostic assay offered as LDT through LenaDx. Clinical laboratory footprint established.' },
                  { year: '2023', title: 'LENA S1® Launched', desc: 'Commercially available diagnostic assay using methylation for detecting non-small cell lung cancer. Clinical laboratory footprint established.' },
                  { year: '2024', title: 'LENA Code™ & LENA Score™ Developed', desc: 'Proprietary normalization engine and risk-scoring algorithm built. ALER-5 dataset assembly initiates.' },
                  { year: '2025', title: 'ALER-5 Validation Completed & KIT Framework', desc: '538,090 observations, 199,800 patient EMRs across 5 cohorts and 3 Illumina platforms. 41-month detection window validated. AUC 0.967. KIT framework manuscript in preparation for publishing.' },
                  { year: '2026', title: 'Pipeline Expansion & Series A', desc: 'qMETHYL Series™ IFU complete. m5C-ESSENTIAL™ patent filed. NEURO-RESTORE protocol initiated. Series A financing open.' },
                ].map((m, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-year">{m.year}</div>
                    <div className="timeline-title">{m.title}</div>
                    <div className="timeline-desc">{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section section-surface">
        <div className="container">
          <span className="eyebrow">Management Team</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>The team behind the science.</h2>
          <div className="cyan-line" />
          <div className="team-grid">
            {TEAM.map((member, i) => (
              <div key={i} className="team-card">
                <div className="team-avatar">{member.initials}</div>
                <div className="team-name">{member.name}</div>
                <div className="team-title">{member.title}</div>
                <p className="team-bio">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Structure */}
      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <span className="eyebrow">Corporate Structure</span>
          <h2 className="section-headline" style={{ marginBottom: 20 }}>Alercell, Inc. and LenaDx — understanding our structure.</h2>
          <div className="cyan-line" />
          <p className="body-md" style={{ marginBottom: 20 }}>
            Alercell, Inc. is the parent biotechnology company — the owner of all intellectual property, the LENA platform, the ALER-5 validation dataset, and the full diagnostic and theranostic pipeline. Alercell conducts research, holds patents, and manages investor relationships and strategic partnerships.
          </p>
          <p className="body-md" style={{ marginBottom: 20 }}>
            LenaDx is Alercell's clinical laboratory brand — the operating entity through which LENA diagnostic assays are offered as Laboratory Developed Tests (LDTs). LenaDx provides the CLIA-certified laboratory infrastructure for commercial test delivery, allowing Alercell to generate revenue while next-generation platforms advance through validation.
          </p>
          <p className="body-md">
            This structure separates clinical laboratory operations from IP development and fundraising, protecting both functions while allowing each to scale appropriately.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-surface">
        <div className="container cta-banner">
          <h2 className="section-headline">Ready to explore the science?</h2>
          <p className="body-lg">Review the full validation dataset, AI methodology, and peer-reviewed publications behind the LENA platform.</p>
          <div className="cta-buttons">
            <Link to="/science" className="btn btn-primary">View Science & Validation</Link>
            <Link to="/investors" className="btn btn-outline">Investor Relations</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
